import { OrderItem, type PowerGenerationProduct } from "@/api/SolarCloudApi";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { useCatalog } from "@/queries/useCatalog";
import { useOrderUuid, useSetOrderUuid } from "@/stores/orderIdStore";
import { useNewOrder } from "@/queries/order/useNewOrder";
import { formatPrice } from "@/util/formatPrice";
import { calculateOrderValues, getOrderDiscountPercentage, setOrderItem } from "@/util/order";
import { useUpdateNewOrder } from "@/queries/order/useUpdateNewOrder";
import { useMemo, useState, useEffect } from "react";
import {
  Minus,
  Plus,
  Zap,
  ArrowRight,
  Shield,
  Lock,
  ChevronLeft,
  ChevronDown,
  Info,
} from "lucide-react";
import applePayLogo from "@/res/logos/Apple-Pay-Mark/apple-pay-logo.png";
import googlePayLogo from "@/res/logos/Google-Pay-Mark/google-pay-logo.png";
import paypalLogo from "@/res/logos/PayPal_Logo_2022/PayPal_Logo_Horizontal_Full_Color_RGB.png";
import payIn4Logo from "@/res/logos/PayPal_Payin4.png";
import paytoLogo from "@/res/logos/payto-logo.png";
import klarnaLogo from "@/res/logos/klarna-logo.png";
import afterpayLogo from "@/res/logos/afterpay-logo.png";
import visaLogo from "@/res/logos/visa-logo.png";
import masterCardLogo from "@/res/logos/mastercard-logo.png";
import amexLogo from "@/res/logos/amex-logo.png";
import WarrantyDialog from "./WarrantyDialog";
import PaymentTermsDialog from "./PaymentTermsDialog";
import VirtualBatteryDialog from "./VirtualBatteryDialog";
import { useBuyPanelsStore } from "../store";
import { TrackableEventNameEnum } from "react-meta-pixel";
import { usePixelContext } from "@/contexts/MetaPixelContext";
import HelpSection from "../components/HelpSection";
import FeatureBadges from "../components/FeatureBadges";
import { useCompleteStep } from "../hook";
import { useNavigate } from "react-router";

type ConfigureSolarSystemProps = {
  isVirtualBatteryIncluded?: boolean;
  hideDefaultIntro?: boolean;
};

export default function ConfigureSolarSystem({
  isVirtualBatteryIncluded = false,
  hideDefaultIntro = false,
}: ConfigureSolarSystemProps) {
  const { data: products } = useCatalog();
  const orderUuid = useOrderUuid();
  const { data: order } = useNewOrder({ uuid: orderUuid! });
  const steps = useBuyPanelsStore((state) => state.steps);
  const getPreviousStep = useBuyPanelsStore((state) => state.getPreviousStep);
  const navigate = useNavigate();

  const pathSegments = window.location.pathname.split("/").filter(Boolean);
  const currentPath = pathSegments.pop() || "";
  const currentStepIndex = steps.findIndex((step) => step.slug === currentPath);
  const hasPreviousStep = currentStepIndex > 0;

  const handleBack = () => {
    const prev = getPreviousStep();
    if (prev) {
      navigate(prev);
    } else {
      window.history.back();
    }
  };

  return (
    <>
      <style>{`input[aria-label="panel-count"]::-webkit-outer-spin-button, input[aria-label="panel-count"]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
      input[aria-label="panel-count"] { -moz-appearance: textfield; }
      `}</style>
      
      <div className="space-y-6 bg-[#0f1317] p-5 sm:p-7 rounded-2xl border border-zinc-800/80">
        {/* Top Header Bar */}
        <div className="flex items-center justify-between text-sm text-gray-400 min-h-6">
          {hasPreviousStep ? (
            <button
              onClick={handleBack}
              className="flex items-center gap-1 hover:text-white transition-colors cursor-pointer text-sm font-medium"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-400 ml-auto">
            <Lock className="w-3.5 h-3.5 text-brand-orange-500" />
            <span>All transactions are secure and encrypted</span>
          </div>
        </div>

        {/* Main Title & Description */}
        {!hideDefaultIntro && (isVirtualBatteryIncluded ? (
          <div>
            <p className="text-xs md:text-sm font-bold tracking-wider text-brand-orange-500 uppercase mb-2">
              VIRTUAL BATTERY INCLUDED
            </p>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-tight mb-3">
              Buy your virtual battery <br />
              <span className="text-brand-orange-500">in 100W increments</span>
            </h1>
            <p className="text-gray-300 text-sm md:text-base leading-relaxed max-w-3xl">
              Every SolarCloud purchase includes both solar and a virtual battery, so you can use the value of your solar whenever you want —{" "}
              <span className="text-brand-orange-500 font-bold">day or night.</span>
            </p>
          </div>
        ) : (
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-3">
              Start saving on your energy bills{" "}
              <span className="text-brand-orange-500">day and night.</span>
            </h1>
            <p className="text-sm sm:text-base text-gray-300 leading-relaxed max-w-3xl">
              Choose the solar system size that suits you. Every purchase includes our virtual battery,
              so you can use the value whenever you need it — day or night.
            </p>
          </div>
        ))}

        {/* Feature Badges */}
        {!hideDefaultIntro && <FeatureBadges />}

        {/* Energy Savings / How It Works Box */}
        {isVirtualBatteryIncluded && (
          <Card className="bg-[#161a1e] border border-zinc-800 mb-6 py-0 rounded-xl">
            <CardContent className="p-5 md:p-6 text-white">
              <p className="font-bold text-base md:text-lg mb-4">How it works</p>
              <div className="space-y-4 text-sm md:text-base">
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-brand-orange-500 text-black font-bold flex items-center justify-center text-xs shrink-0 mt-0.5">
                    1
                  </span>
                  <div>
                    <p className="font-semibold text-white">Choose your size</p>
                    <p className="text-gray-400 text-xs md:text-sm">
                      Buy in 100W increments — solar and virtual battery included.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-brand-orange-500 text-black font-bold flex items-center justify-center text-xs shrink-0 mt-0.5">
                    2
                  </span>
                  <div>
                    <p className="font-semibold text-white">We generate your energy</p>
                    <p className="text-gray-400 text-xs md:text-sm">
                      Your solar is turned into real dollar credits on your bill.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-brand-orange-500 text-black font-bold flex items-center justify-center text-xs shrink-0 mt-0.5">
                    3
                  </span>
                  <div>
                    <p className="font-semibold text-white">Use it anytime</p>
                    <p className="text-gray-400 text-xs md:text-sm">
                      Your virtual battery lets you use that value whenever you need it — day or night.
                    </p>
                  </div>
                </div>
              </div>
              <VirtualBatteryDialog />
            </CardContent>
          </Card>
        )}

        {/* Select your solar capacity */}
        {isVirtualBatteryIncluded && (
          <div className="mb-6 rounded-xl border border-brand-orange-500/30 bg-black/20 p-4 md:p-5 text-white">
            <p className="font-bold text-base md:text-lg mb-1">Select your solar capacity</p>
            <p className="text-xs md:text-sm text-gray-400 mb-4">
              Your virtual battery is included with every purchase.
            </p>
            <div className="rounded-xl border border-zinc-800 bg-black/40 p-4 md:p-5 flex items-center justify-between">
              <div>
                <p className="font-semibold text-white text-sm md:text-base mb-2">
                  Each panel includes:
                </p>
                <ul className="space-y-1 text-xs md:text-sm text-gray-300">
                  <li>100W solar</li>
                  <li>Virtual battery</li>
                </ul>
              </div>
              <div className="shrink-0 flex items-center justify-center pl-4 pr-2">
                <svg
                  className="w-28 h-20 md:w-36 md:h-24 text-brand-orange-500 overflow-visible"
                  viewBox="0 0 140 95"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    filter:
                      "drop-shadow(0 0 6px rgba(245, 166, 35, 0.75)) drop-shadow(0 0 16px rgba(245, 166, 35, 0.35))",
                  }}
                >
                  {/* Cloud Outline */}
                  <path
                    d="M 28 72 H 112 C 123 72 131 64 131 53 C 131 43 124 35 114 34 C 111 20 99 10 84 10 C 72 10 62 17 56 26 C 51 22 44 20 37 22 C 26 25 18 35 18 47 C 18 61 24 72 28 72 Z"
                    stroke="currentColor"
                    strokeWidth="3.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  {/* Battery Top Terminal Nub */}
                  <rect
                    x="63"
                    y="22"
                    width="14"
                    height="5"
                    rx="2"
                    fill="#0e1015"
                    stroke="currentColor"
                    strokeWidth="3"
                  />

                  {/* Battery Main Body */}
                  <rect
                    x="53"
                    y="26"
                    width="34"
                    height="51"
                    rx="7"
                    fill="#0e1015"
                    stroke="currentColor"
                    strokeWidth="3.2"
                  />

                  {/* Lightning Bolt */}
                  <path
                    d="M 72 35 L 61 50 H 69 L 66 67 L 79 49 H 71 Z"
                    stroke="currentColor"
                    strokeWidth="2.8"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    fill="currentColor"
                  />
                </svg>
              </div>
            </div>
          </div>
        )}

        {isVirtualBatteryIncluded && (
          <Card className="bg-[#161a1e] border border-zinc-800 mb-5 py-0 rounded-xl">
            <CardContent className="p-4 md:p-6">
              <div className="flex gap-4 items-start">
                <div className="shrink-0">
                  <div className="w-10 h-10 rounded-full border-2 border-brand-orange-500 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-brand-orange-500" />
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-white mb-1">Reserve your solar with 10% today</p>
                  <p className="text-sm text-gray-400">Start saving now. Pay the rest when your system is ready.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Warranty Options / Product List */}
        {products && <WarrantyOptions products={products} />}

        {/* 100% Deposit Refund Policy Card */}
        <Card className="bg-[#1c1917] border-2 border-brand-orange-500 rounded-xl py-0 shadow-lg shadow-black/40">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full border border-brand-orange-500/40 bg-brand-orange-500/10 flex items-center justify-center text-brand-orange-500 shrink-0">
                <Shield className="w-8 h-8" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-base sm:text-lg font-bold text-white">
                    100% deposit refund policy
                  </h3>
                  <div className="flex items-center gap-1">
                    <PaymentTermsDialog showChevron={true} />
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-gray-400 mt-1">
                  Change your mind? No problem. Get a full refund of your 10% deposit at any time before your solar is allocated.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help & Specialist Actions */}
        <HelpSection />
      </div>
    </>
  );
}

type WarrantyOptionsProps = {
  products: PowerGenerationProduct[];
};

function WarrantyOptions({ products }: WarrantyOptionsProps) {
  const orderUuid = useOrderUuid();
  const { data: order } = useNewOrder({ uuid: orderUuid! });
  const { mutate: updateOrder } = useUpdateNewOrder({
    refetchQueries: false,
  });
  const { pixel } = usePixelContext();

  const items = useMemo(() => {
    if (!products || !order) return [];
    return products.map((product) => {
      const orderItem = order?.items?.find(
        (item) => item.product?.uuid === product.uuid
      );
      return {
        product,
        units: orderItem?.units ?? 0,
        unitPrice: orderItem?.unitPrice ?? product.wattPriceCurrent ?? 0,
      };
    });
  }, [products, order]);

  const handleUpdateItems = (updatedItem: OrderItem) => {
    const updatedItems = setOrderItem(items, updatedItem);
    const orderWithItems = {
      ...order,
      items: updatedItems.filter((item) => item.units! > 0),
    };
    const discountPercentage = getOrderDiscountPercentage(
      order,
      orderWithItems.items
    );

    const orderValues = calculateOrderValues(
      orderWithItems,
      discountPercentage
    );
    updateOrder({
      ...orderWithItems,
      ...orderValues,
    });

    // Keep local store in sync so the cookie subscription triggers.
    try {
      useBuyPanelsStore.getState().setData({ order: orderWithItems });
    } catch (e) {
    }

    if (pixel) {
      const contents = orderWithItems.items
        .filter(
          (item): item is { product: { uuid: string }; units?: number } =>
            !!item.product?.uuid
        )
        .map((item) => ({
          id: item.product.uuid,
          quantity: (item.units ?? 0) / 100,
        }));

      pixel.trackEvent(TrackableEventNameEnum.AddToCart, {
        contents,
        currency: "AUD",
        value: orderValues.totalPrice,
      });
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      {items.map((item) => (
        <WarrantyCard
          key={item.product.uuid!}
          item={item}
          onUpdate={handleUpdateItems}
        />
      ))}
    </div>
  );
}

type WarrantyCardProps = {
  item: {
    product: PowerGenerationProduct;
    units: number;
    unitPrice: number;
  };
  onUpdate: (item: OrderItem) => void;
};

function WarrantyCard({ item, onUpdate }: WarrantyCardProps) {
  const { product, units, unitPrice } = item;
  const quantity = product.wattPanelSize ?? 1;
  const derivedCount = Math.round((units ?? 0) / quantity);
  const [localCount, setLocalCount] = useState<number>(derivedCount);
  useEffect(() => {
    setLocalCount(derivedCount);
  }, [derivedCount]);

  const orderUuid = useOrderUuid();
  const setOrderUuid = useSetOrderUuid();
  const { data: order } = useNewOrder({ uuid: orderUuid! });
  const completeStep = useCompleteStep();
  const { pixel } = usePixelContext();

  const { mutate: proceedToCheckout, isPending } = useUpdateNewOrder({
    mutationConfig: {
      onSuccess: (data) => {
        if (pixel) {
          pixel.trackEvent(TrackableEventNameEnum.InitiateCheckout, {
            currency: "AUD",
            value: data.totalPrice,
            num_items: data.items?.reduce(
              (total, item) => total + (item.units ?? 0) / 100,
              0
            ),
          });
        }
        setOrderUuid(data.uuid!);
        completeStep();
      },
    },
  });

  const handleBuyNow = () => {
    if (order) {
      proceedToCheckout(order);
    }
  };

  const totalPrice = localCount * unitPrice * quantity;
  const pricePerPanel = unitPrice * quantity; // Price per panel = price per watt × watts per panel
  const systemSize = (localCount * quantity) / 1000; // Convert watts to kW
  // Get warranty years from product termYears property
  const warrantyYears = product.termYears || 20;

  const isAvailable = product.enabled === true;

  const discountPercent = getOrderDiscountPercentage(order);
  const isDiscountApplied = isAvailable && discountPercent > 0;
  const discountedPricePerPanel = isDiscountApplied
    ? pricePerPanel * (1 - discountPercent / 100)
    : pricePerPanel;

  const depositPercentage =
    order?.items && order.items.length > 0
      ? order.items[0].pricing?.depositPercentage ?? 10
      : 10;
  const discountedDeposit = isDiscountApplied
    ? (totalPrice * (1 - discountPercent / 100) * depositPercentage) / 100
    : (totalPrice * depositPercentage) / 100;

  const handleIncrement = () => {
    if (!isAvailable) return;
    const newCount = (localCount || 0) + 1;
    setLocalCount(newCount);
    onUpdate({ product, units: newCount * quantity, unitPrice });
  };

  const handleDecrement = () => {
    if (!isAvailable) return;
    if ((localCount || 0) > 0) {
      const newCount = Math.max(0, (localCount || 0) - 1);
      setLocalCount(newCount);
      onUpdate({ product, units: newCount * quantity, unitPrice });
    }
  };

  const isSelected = localCount > 0;

  return (
    <Card
      className={`py-0 transition-all rounded-xl ${
        !isAvailable
          ? "opacity-60 cursor-not-allowed border-zinc-800 bg-[#161a1e]"
          : isSelected
          ? "border-2 border-brand-orange-500 bg-[#1c1917] shadow-lg shadow-black/40"
          : "border border-zinc-800/90 bg-[#161a1e] hover:border-zinc-700"
      }`}
    >
      <CardContent className="p-4 sm:p-6 flex flex-col justify-between">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                {warrantyYears} Year Warranty Solar Panel
              </h3>
              {product.label && (
                <span
                  className="text-xs rounded text-white px-2 py-0.5 font-semibold"
                  style={{ backgroundColor: product.labelColour }}
                >
                  {product.label}
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm text-gray-400">
              Premium commercial grade solar with extended warranty.{" "}
              <span className="inline-flex text-sm align-middle text-white">
                <WarrantyDialog />
              </span>
            </p>
          </div>
          <div className="text-left md:text-right">
            {isDiscountApplied ? (
              <p className="text-xs sm:text-sm text-gray-300">
                Price:{" "}
                <span className="text-xs text-gray-400 line-through decoration-gray-400 mr-1">
                  {formatPrice(pricePerPanel)}
                </span>
                <span className="text-xs font-semibold text-red-500 mr-1">
                  -{Math.round(discountPercent)}%
                </span>
                <span className="font-semibold text-white">
                  {formatPrice(discountedPricePerPanel)}
                </span>{" "}
                per SolarCloud solar panel
              </p>
            ) : (
              <p className="text-gray-300 text-xs sm:text-sm">
                Price: <span className="font-semibold text-white">{formatPrice(pricePerPanel)}</span> per SolarCloud
                <br className="hidden md:inline" /> solar panel
              </p>
            )}
          </div>
        </div>

        {/* Counter and Capacity calculation */}
        {isAvailable && (
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 pt-2">
            <div className="flex items-center gap-1.5 bg-[#090b0d] border border-zinc-800 rounded-lg p-1.5">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded text-gray-300 hover:text-white hover:bg-zinc-800"
                onClick={handleDecrement}
                disabled={localCount === 0 || !isAvailable}
                aria-label="Decrease quantity"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <div className="bg-[#181c20] border border-zinc-700/80 rounded px-3 py-1 flex items-center justify-center">
                <input
                  type="text"
                  inputMode="numeric"
                  value={localCount}
                  onInput={(e) => {
                    if (!isAvailable) return;
                    const raw = e.currentTarget.value || "";
                    const numeric = raw.replace(/[^\d]/g, "");
                    const normalized = numeric.replace(/^0+(?=\d)/, "") || "0";
                    const newQty = Math.max(0, parseInt(normalized, 10));
                    setLocalCount(newQty);
                    onUpdate({ product, units: newQty * quantity, unitPrice });
                  }}
                  aria-label="panel-count"
                  className="bg-transparent text-center w-10 font-bold text-base sm:text-lg text-white outline-none"
                  disabled={!isAvailable}
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded text-gray-300 hover:text-white hover:bg-zinc-800"
                onClick={handleIncrement}
                disabled={!isAvailable}
                aria-label="Increase quantity"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            <span className="text-xl font-light text-gray-500">=</span>

            <div className="flex flex-col">
              <span className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                {systemSize.toFixed(1)} kW
              </span>
              <span className="text-xs text-gray-400">
                {localCount} {localCount === 1 ? "panel" : "panels"}
              </span>
            </div>

            {/* Mobile-only deposit info */}
            {localCount > 0 && (
              <div className="md:hidden w-full mt-3 pt-3 border-t border-zinc-800">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    Deposit ({depositPercentage.toFixed(0)}%):
                  </span>
                  <span className="text-base font-bold text-brand-orange-500">
                    {formatPrice(discountedDeposit)}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
